import { cn, useTranslation } from '@hbs-us/ui';
import { useEffect, useState } from 'react';

import type { NotificationSettings } from '../notifications/contracts.js';
import {
  useNotificationSettingsQuery,
  useUpdateSettingsMutation,
} from '../notifications/useNotificationsQuery.js';

/**
 * Settings → Notifications (Surface C-web — D-13s). A master push toggle + per-category toggles
 * (Announcements / Jobs / Invoices) that persist OPTIMISTICALLY with a "Saved." confirm. The In-app
 * bell + Account & security rows are ALWAYS-ON: they render `aria-disabled` with a visible + SR
 * "Can't be turned off" reason and cannot be flipped (the server also rejects it). EN+ES,
 * token-driven, zero hardcoded hex; the title+description column grows so longer ES strings never
 * clip (I18N-05).
 */

/** An accessible toggle (role="switch") repointed to tokens — no third-party Switch primitive. */
function Toggle({
  checked,
  onChange,
  labelledBy,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  labelledBy: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        checked ? 'bg-primary' : 'bg-secondary',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block size-5 rounded-full bg-background transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

interface SettingRowProps {
  id: string;
  title: string;
  description: string;
  /** When set, the row is always-on: locked + a visible reason; no toggle is rendered. */
  alwaysOnReason?: string;
  checked?: boolean;
  onChange?: (next: boolean) => void;
}

function SettingRow({
  id,
  title,
  description,
  alwaysOnReason,
  checked,
  onChange,
}: SettingRowProps) {
  const locked = alwaysOnReason !== undefined;
  return (
    <div
      className="flex items-center justify-between gap-4 border-border/60 border-b py-4 last:border-b-0"
      aria-disabled={locked || undefined}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span id={id} className="font-semibold text-foreground text-sm">
          {title}
        </span>
        <span className="text-muted-foreground text-sm">{description}</span>
        {locked ? (
          <span className="mt-0.5 text-muted-foreground text-xs italic">{alwaysOnReason}</span>
        ) : null}
      </div>
      {locked ? (
        <span
          role="switch"
          aria-checked="true"
          aria-disabled="true"
          aria-labelledby={id}
          tabIndex={0}
          className="relative inline-flex h-6 w-11 shrink-0 cursor-not-allowed items-center rounded-full bg-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <span
            aria-hidden="true"
            className="inline-block size-5 translate-x-5 rounded-full bg-background/70"
          />
        </span>
      ) : (
        <Toggle checked={checked ?? false} onChange={onChange ?? (() => {})} labelledBy={id} />
      )}
    </div>
  );
}

export function NotificationSettingsScreen() {
  const { t } = useTranslation('notifications');
  const { data: settings } = useNotificationSettingsQuery();
  const updateSettings = useUpdateSettingsMutation();
  const [savedVisible, setSavedVisible] = useState(false);

  // Briefly show "Saved." after a successful persist (optimistic confirm).
  useEffect(() => {
    if (!updateSettings.isSuccess) return;
    setSavedVisible(true);
    const id = window.setTimeout(() => setSavedVisible(false), 2000);
    return () => window.clearTimeout(id);
  }, [updateSettings.isSuccess]);

  const current: NotificationSettings = settings ?? {
    masterPush: true,
    announcements: true,
    jobs: true,
    invoices: true,
  };

  const patch = (next: Partial<NotificationSettings>) =>
    updateSettings.mutate({ ...current, ...next });

  return (
    <section className="flex max-w-2xl flex-col gap-6 p-6 lg:p-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {t('settings.title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">{t('settings.subtitle')}</p>
        </div>
        <span
          aria-live="polite"
          className={cn(
            'font-semibold text-success text-sm transition-opacity',
            savedVisible ? 'opacity-100' : 'opacity-0',
          )}
        >
          {savedVisible ? t('settings.saved') : ''}
        </span>
      </header>

      <div className="rounded-md border border-border bg-card px-4">
        <SettingRow
          id="setting-master-push"
          title={t('settings.masterPush.title')}
          description={t('settings.masterPush.description')}
          checked={current.masterPush}
          onChange={(next) => patch({ masterPush: next })}
        />
        <SettingRow
          id="setting-in-app-bell"
          title={t('settings.inAppBell.title')}
          description={t('settings.inAppBell.description')}
          alwaysOnReason={t('settings.alwaysOnHint')}
        />
      </div>

      <div className="flex flex-col gap-2">
        <h2 className="px-1 font-semibold text-muted-foreground text-sm uppercase tracking-wide">
          {t('settings.categoriesLabel')}
        </h2>
        <div className="rounded-md border border-border bg-card px-4">
          <SettingRow
            id="setting-announcements"
            title={t('settings.announcements.title')}
            description={t('settings.announcements.description')}
            checked={current.announcements}
            onChange={(next) => patch({ announcements: next })}
          />
          <SettingRow
            id="setting-jobs"
            title={t('settings.jobs.title')}
            description={t('settings.jobs.description')}
            checked={current.jobs}
            onChange={(next) => patch({ jobs: next })}
          />
          <SettingRow
            id="setting-invoices"
            title={t('settings.invoices.title')}
            description={t('settings.invoices.description')}
            checked={current.invoices}
            onChange={(next) => patch({ invoices: next })}
          />
          <SettingRow
            id="setting-account"
            title={t('settings.account.title')}
            description={t('settings.account.description')}
            alwaysOnReason={t('settings.alwaysOnHint')}
          />
        </div>
      </div>
    </section>
  );
}
